import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X } from 'lucide-react';
import styles from './SearchBar.module.css';

const SearchBar = ({
  onSearch,
  onCommit,
  searchTerm = '',
  placeholder = 'Search resources...',
  debounceMs = 300,
  className = ''
}) => {
  const [inputValue, setInputValue] = useState(searchTerm);
  const inputRef = useRef(null);
  const onSearchRef = useRef(onSearch);
  const [keepFocus, setKeepFocus] = useState(false);

  // Keep latest onSearch in a ref so debounce doesn't need to be recreated
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  // Debounced search function
  const debouncedSearchRef = useRef(null);
  if (!debouncedSearchRef.current) {
    debouncedSearchRef.current = debounce((value) => {
      onSearchRef.current(value);
    }, debounceMs);
  }

  // Note: We intentionally avoid syncing searchTerm prop back into the input
  // to prevent parent state updates from stealing focus while typing.
  // The input is now self-managed; parent receives updates via onSearch.

  // Handle input change with debouncing
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setKeepFocus(true);
    // Only search when 3+ chars, or clear when empty
    if (value.length >= 3) {
      debouncedSearchRef.current(value);
    } else if (value.length === 0) {
      onSearch('');
    }
  };

  // Handle clear search
  const handleClear = () => {
    setInputValue('');
    if (onCommit) {
      onCommit('');
    } else {
      onSearch('');
    }
    // Ensure focus remains on the input after clearing
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle keyboard events
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleClear();
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (onCommit) {
        onCommit(inputValue);
      } else {
        onSearch(inputValue);
      }
      setKeepFocus(true);
    }
  };

  // Handle wrapper click to focus input
  const handleWrapperClick = (e) => {
    // Don't interfere with button clicks
    if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
      return;
    }
    // Focus the input
    const input = e.currentTarget.querySelector('input');
    if (input) {
      input.focus();
      setKeepFocus(true);
    }
  };

  // Re-assert focus if it was unintentionally lost while keepFocus is true
  useEffect(() => {
    if (!keepFocus) return;
    const el = inputRef.current;
    if (!el) return;
    if (document.activeElement !== el) {
      try { el.focus({ preventScroll: true }); } catch { el.focus(); }
    }
  });

  return (
    <div className={`${styles.searchContainer} ${className}`}>
      <div
        className={styles.searchInputWrapper}
        onClick={handleWrapperClick}
      >
        <Search
          className={styles.searchIcon}
          size={20}
          aria-hidden="true"
          style={{ pointerEvents: 'none' }}
        />
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={styles.searchInput}
          aria-label="Search resources"
          aria-describedby="search-help"
          autoComplete="off"
          onFocus={() => setKeepFocus(true)}
          onBlur={() => setKeepFocus(false)}
          ref={inputRef}
        />
        {inputValue && inputValue.trim() !== '' && (
          <button
            type="button"
            onClick={handleClear}
            className={styles.clearButton}
            aria-label="Clear search"
            title="Clear search"
          >
            <X size={18} aria-hidden="true" />
          </button>
        )}
      </div>
      <div id="search-help" className={styles.srOnly}>
        Use this field to search through resources. Results will update as you type.
      </div>
    </div>
  );
};

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default SearchBar;